import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, ClipboardEvent } from 'react';
import { Checkbox, Col, Form, Input, Row, Select, Typography } from 'antd';
import type { FormInstance } from 'antd';
import type { AllyDocumentType, PersonType } from '@goelectric/shared-types';
import {
  parseCompoundDocument,
  type CompoundDocumentError,
  type CreateAllyFormState,
} from '../utils/fiscal';
import { DOCUMENT_TYPE_LABELS, PERSON_TYPE_LABELS } from '../utils/format';

const PERSON_TYPE_OPTIONS: { value: PersonType; label: string }[] = (
  ['NATURAL', 'JURIDICA'] as const
).map((value) => ({ value, label: PERSON_TYPE_LABELS[value] }));

/**
 * Orden de la spec: CC · CE, con CC como default. **NIT no va**: el operador no elige
 * "NIT", declara si está inscrito en el RUT (checkbox); `buildCreateAllyPayload` traduce.
 */
const NATURAL_DOCUMENT_TYPE_OPTIONS: { value: AllyDocumentType; label: string }[] = (
  ['CC', 'CE'] as const
).map((value) => ({ value, label: DOCUMENT_TYPE_LABELS[value] }));

/** Mensaje explícito por cada fallo del parseo: nunca adivinar el compuesto. */
const PASTE_ERROR_MESSAGES: Record<CompoundDocumentError, string> = {
  EMPTY: 'No se pegó ningún documento.',
  NO_SEPARATOR:
    'No se pudo separar: falta el guion entre el número y el DV (ej. 901234567-8).',
  INVALID_NUMBER:
    'No se pudo separar: la parte del número tiene caracteres que no son dígitos.',
  INVALID_CHECK_DIGIT: 'No se pudo separar: el DV debe ser un único dígito (0–9).',
};

interface AllyFiscalFieldsProps {
  form: FormInstance<CreateAllyFormState>;
}

/**
 * Bloque de identidad fiscal del alta de aliado (US-2.2), controlado por el `Form`:
 * el condicional por tipo de persona, el checkbox "Inscrito en el RUT" (natural), los
 * labels dinámicos del número, el DV (solo NIT) y la validación de requeridos por caso.
 *
 * El operador no elige "NIT": para persona natural tilda si está inscrito en el RUT y el
 * DV aparece; `buildCreateAllyPayload` hace la traducción a NIT + DV. Este bloque solo
 * produce estado.
 */
export function AllyFiscalFields({ form }: AllyFiscalFieldsProps) {
  const personType = Form.useWatch('personType', form) ?? 'NATURAL';
  const watchedDocumentType = Form.useWatch('documentType', form);
  const registeredInRut = Form.useWatch('registeredInRut', form) ?? false;
  const isJuridica = personType === 'JURIDICA';
  const selectedDocumentType: AllyDocumentType = watchedDocumentType ?? 'CC';
  // El label/placeholder del número NO cambia al tildar el RUT: para la natural es siempre el
  // CC/CE elegido (el operador sigue escribiendo su cédula); para la jurídica, NIT.
  const numberDocumentType: AllyDocumentType = isJuridica ? 'NIT' : selectedDocumentType;
  // El tipo efectivo (DV y contrato): la jurídica y la natural inscrita en el RUT son NIT.
  const effectiveDocumentType: AllyDocumentType =
    isJuridica || registeredInRut ? 'NIT' : selectedDocumentType;
  const [pasteNotice, setPasteNotice] = useState<string | null>(null);

  // Reset por transición de tipo de persona: al volver a NATURAL el documento arranca en CC
  // (default de la spec) y el RUT destildado; al pasar a jurídica el tick no debe filtrarse
  // al payload. Solo en un CAMBIO real, no en el montaje: el modal de edición (TG-118) monta
  // este bloque ya prellenado y no debe pisarle el RUT/DV.
  const mountedPersonType = useRef(personType);
  useEffect(() => {
    if (mountedPersonType.current === personType) {
      return;
    }
    mountedPersonType.current = personType;
    setPasteNotice(null);
    form.setFieldValue('registeredInRut', false);
    if (personType === 'NATURAL') {
      form.setFieldValue('documentType', 'CC');
    }
  }, [personType, form]);

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text').trim();
    // Pegado de un número simple: no hay nada que partir, se comporta normal.
    if (text === '' || /^\d+$/.test(text)) {
      return;
    }
    event.preventDefault();
    const parsed = parseCompoundDocument(text);
    if (!parsed.ok) {
      setPasteNotice(PASTE_ERROR_MESSAGES[parsed.error]);
      return;
    }
    form.setFieldValue('documentNumber', parsed.documentNumber);
    // El compuesto trae DV: en natural eso significa que está inscrito en el RUT (se tilda
    // solo, el usuario puede destildar); en jurídica ya es NIT. El DV nunca se descarta.
    if (!isJuridica) {
      form.setFieldValue('registeredInRut', true);
    }
    form.setFieldValue('checkDigit', parsed.checkDigit);
    setPasteNotice(
      `Documento separado: número ${parsed.documentNumber} · DV ${parsed.checkDigit}.`,
    );
  };

  // El número es el mismo campo en ambos casos; en natural comparte fila con el tipo de
  // documento, en jurídica va solo a ancho completo (no hay tipo que lo acompañe).
  const documentNumberField = (
    <Form.Item
      label={DOCUMENT_TYPE_LABELS[numberDocumentType]}
      name="documentNumber"
      extra={
        pasteNotice ? (
          <Typography.Text type="warning" role="status">
            {pasteNotice}
          </Typography.Text>
        ) : undefined
      }
      // Máscara en runtime: el valor guardado (y mostrado) nunca contiene guiones, espacios
      // ni otros caracteres. El pegado compuesto no pasa por acá: su `onPaste` hace
      // `preventDefault` y setea los campos saneados. La regla `pattern` queda de red.
      getValueFromEvent={(event: ChangeEvent<HTMLInputElement>) =>
        event.target.value.replace(/\D/g, '')
      }
      rules={[
        { required: true, message: 'Ingresa el número de documento.' },
        { pattern: /^\d+$/, message: 'Solo dígitos, sin puntos ni guiones.' },
      ]}
    >
      <Input
        inputMode="numeric"
        autoComplete="off"
        placeholder={numberDocumentType === 'NIT' ? '900123456' : 'Número sin puntos ni guiones'}
        onChange={() => setPasteNotice(null)}
        onPaste={handlePaste}
      />
    </Form.Item>
  );

  return (
    <>
      <Form.Item label="Tipo de persona" name="personType" rules={[{ required: true }]}>
        <Select options={PERSON_TYPE_OPTIONS} />
      </Form.Item>

      {isJuridica ? (
        <Form.Item
          label="Razón social del aliado"
          name="legalName"
          preserve={false}
          rules={[{ required: true, message: 'Ingresa la razón social.' }]}
        >
          <Input autoComplete="off" placeholder="Introduzca la razón social del aliado" />
        </Form.Item>
      ) : (
        <Row gutter={16}>
          {/* Nombres y apellidos al mismo nivel; en pantallas angostas se apilan (xs) en vez de encogerse. */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Nombres del aliado"
              name="firstName"
              preserve={false}
              rules={[{ required: true, message: 'Ingresa los nombres.' }]}
            >
              <Input autoComplete="off" placeholder="Introduzca el nombre(s) del aliado" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Apellidos del aliado"
              name="lastName"
              preserve={false}
              rules={[{ required: true, message: 'Ingresa los apellidos.' }]}
            >
              <Input autoComplete="off" placeholder="Introduzca los apellidos del aliado" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {isJuridica ? (
        // Sin "Tipo de documento" (solo natural): el número ocupa la fila completa.
        documentNumberField
      ) : (
        <Row gutter={16}>
          {/* Tipo y número al mismo nivel; en pantallas angostas se apilan (xs). */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Tipo de documento"
              name="documentType"
              preserve={false}
              rules={[{ required: true, message: 'Selecciona el tipo de documento.' }]}
            >
              <Select options={NATURAL_DOCUMENT_TYPE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>{documentNumberField}</Col>
        </Row>
      )}

      {!isJuridica && (
        <Form.Item name="registeredInRut" valuePropName="checked" preserve={false}>
          <Checkbox onChange={() => setPasteNotice(null)}>Inscrito en el RUT</Checkbox>
        </Form.Item>
      )}

      {effectiveDocumentType === 'NIT' && (
        <Form.Item
          label="Dígito de verificación (DV)"
          name="checkDigit"
          preserve={false}
          tooltip="Dígito que figura en el RUT. Un solo dígito (0–9)."
          // Por UI es imposible pasar de un dígito: `maxLength` lo corta de forma nativa
          // (teclado y pegado) y `normalize` vuelve a recortar a 1 y descarta no-dígitos,
          // dejando el estado como número (contrato `checkDigit: number`, sin cambios).
          normalize={(value) => {
            const digit = String(value ?? '').replace(/\D/g, '').slice(0, 1);
            return digit === '' ? undefined : Number(digit);
          }}
          rules={[
            { required: true, message: 'Ingresa el DV del NIT.' },
            { type: 'number', min: 0, max: 9, message: 'El DV es un solo dígito (0–9).' },
          ]}
        >
          {/* Un solo dígito: ancho fijo y chico, no la fila completa. */}
          <Input inputMode="numeric" maxLength={1} autoComplete="off" style={{ width: 100 }} />
        </Form.Item>
      )}
    </>
  );
}
